import styled, { css } from 'styled-components'
import chroma from 'chroma-js'

import Icon from '../Icon'
import Link from '../Link'
import breakpoints from '../../theme/breakpoints'

export enum ActionButtonVariant {
  primary = 'primary',
  secondary = 'secondary',
  white = 'white',
  whiteWithBorder = 'whiteWithBorder',
  black = 'black',
  danger = 'danger',
}

export enum ActionButtonLook {
  contained = 'contained',
  outlined = 'outlined',
}

export const ActionButton = styled(Link)<{
  $variant?: ActionButtonVariant
  $look?: ActionButtonLook
  $noTextOnMobile?: boolean
  $bigPadding?: boolean
  disabled?: boolean
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: ${(props) => (props.$noTextOnMobile ? '0.9rem 1.6rem' : '0.9rem')};
  padding: ${(props) => props.$bigPadding && '0.9rem 2.5rem'};
  border-radius: 4rem;
  border-width: 1px;
  border-style: solid;
  transition:
    border-color 0.3s,
    background-color 0.3s,
    color 0.3s;

  ${(props) => {
    switch (props.$variant) {
      case ActionButtonVariant.primary:
        switch (props.$look) {
          case ActionButtonLook.contained:
            return css`
              border: 1px solid ${(props) => props.theme.colors.darkOrange};
              color: ${(props) => props.theme.colors.pureWhite};
              background-color: ${(props) => props.theme.colors.darkOrange};
            `
          case ActionButtonLook.outlined:
            return css`
              border: 1px solid ${(props) => props.theme.colors.darkOrange};
              color: ${(props) => props.theme.colors.darkOrange};
              background-color: transparent;
            `
          default:
            return css``
        }
      case ActionButtonVariant.secondary:
        switch (props.$look) {
          case ActionButtonLook.contained:
            return css`
              border: 1px solid ${(props) => props.theme.colors.neonBlue};
              color: ${(props) => props.theme.colors.pureWhite};
              background-color: ${(props) => props.theme.colors.neonBlue};
            `
          case ActionButtonLook.outlined:
            return css`
              border: 1px solid ${(props) => props.theme.colors.neonBlue};
              color: ${(props) => props.theme.colors.neonBlue};
              background-color: transparent;
            `
          default:
            return css``
        }
      case ActionButtonVariant.white:
        switch (props.$look) {
          case ActionButtonLook.contained:
            return css`
              border: 1px solid ${(props) => props.theme.colors.pureWhite};
              color: ${(props) => props.theme.colors.neonBlue};
              background-color: ${(props) => props.theme.colors.pureWhite};
            `
          case ActionButtonLook.outlined:
            return css`
              border: 1px solid ${(props) => props.theme.colors.pureWhite};
              color: ${(props) => props.theme.colors.pureWhite};
              background-color: transparent;
            `
          default:
            return css``
        }
      case ActionButtonVariant.whiteWithBorder:
        switch (props.$look) {
          case ActionButtonLook.contained:
            return css`
              border: 1px solid ${(props) => props.theme.colors.neonBlue};
              color: ${(props) => props.theme.colors.neonBlue};
              background-color: ${(props) => props.theme.colors.pureWhite};
            `
          case ActionButtonLook.outlined:
            return css`
              border: 1px solid ${(props) => props.theme.colors.pureWhite};
              color: ${(props) => props.theme.colors.pureWhite};
              background-color: transparent;
            `
          default:
            return css``
        }
      case ActionButtonVariant.black:
        switch (props.$look) {
          case ActionButtonLook.contained:
            return css`
              border: 1px solid ${(props) => props.theme.colors.nightRider};
              color: ${(props) => props.theme.colors.pureWhite};
              background-color: ${(props) => props.theme.colors.nightRider};
            `
          case ActionButtonLook.outlined:
            return css`
              border: 1px solid ${(props) => props.theme.colors.nightRider};
              color: ${(props) => props.theme.colors.nightRider};
              background-color: transparent;
            `
          default:
            return css``
        }
      case ActionButtonVariant.danger:
        switch (props.$look) {
          case ActionButtonLook.contained:
            return css`
              border: 1px solid ${(props) => props.theme.colors.crimson};
              color: ${(props) => props.theme.colors.pureWhite};
              background-color: ${(props) => props.theme.colors.crimson};
            `
          case ActionButtonLook.outlined:
            return css`
              border: 1px solid ${(props) => props.theme.colors.crimson};
              color: ${(props) => props.theme.colors.crimson};
              background-color: transparent;
            `
          default:
            return css``
        }
    }
  }}

  ${(props) =>
    !props.disabled &&
    css`
      cursor: pointer;
      &:hover:not(:disabled),
      a:hover &:not(:disabled),
      a:focus-visible &:not(:disabled),
      &:focus-visible:not(:disabled) {
        ${() => {
          switch (props.$variant) {
            case ActionButtonVariant.primary:
              switch (props.$look) {
                case ActionButtonLook.contained:
                  return css`
                    background: ${(props) =>
                      chroma(props.theme.colors.darkOrange).darken(0.2).hex()};
                  `
                case ActionButtonLook.outlined:
                  return css`
                    border-color: ${(props) => props.theme.colors.darkOrange};
                    color: ${(props) => props.theme.colors.pureWhite};
                    background-color: ${(props) => props.theme.colors.darkOrange};
                  `
                default:
                  return css``
              }
            case ActionButtonVariant.secondary:
              switch (props.$look) {
                case ActionButtonLook.contained:
                  return css`
                    background: ${(props) => chroma(props.theme.colors.neonBlue).darken(0.2).hex()};
                  `
                case ActionButtonLook.outlined:
                  return css`
                    border-color: ${(props) => props.theme.colors.neonBlue};
                    color: ${(props) => props.theme.colors.pureWhite};
                    background-color: ${(props) => props.theme.colors.neonBlue};
                  `
                default:
                  return css``
              }
            case ActionButtonVariant.white:
              switch (props.$look) {
                case ActionButtonLook.contained:
                  return css`
                    background: ${(props) =>
                      chroma(props.theme.colors.pureWhite).darken(0.2).hex()};
                  `
                case ActionButtonLook.outlined:
                  return css`
                    border-color: ${(props) => props.theme.colors.pureWhite};
                    color: ${(props) => props.theme.colors.neonBlue};
                    background-color: ${(props) => props.theme.colors.pureWhite};
                  `
                default:
                  return css``
              }
            case ActionButtonVariant.whiteWithBorder:
              switch (props.$look) {
                case ActionButtonLook.contained:
                  return css`
                    background: ${(props) =>
                      chroma(props.theme.colors.pureWhite).darken(0.2).hex()};
                  `
                case ActionButtonLook.outlined:
                  return css`
                    border-color: ${(props) => props.theme.colors.pureWhite};
                    color: ${(props) => props.theme.colors.neonBlue};
                    background-color: ${(props) => props.theme.colors.pureWhite};
                  `
                default:
                  return css``
              }
            case ActionButtonVariant.black:
              switch (props.$look) {
                case ActionButtonLook.contained:
                  return css`
                    background: ${(props) =>
                      chroma(props.theme.colors.nightRider).darken(0.2).hex()};
                  `
                case ActionButtonLook.outlined:
                  return css`
                    border-color: ${(props) => props.theme.colors.nightRider};
                    color: ${(props) => props.theme.colors.pureWhite};
                    background-color: ${(props) => props.theme.colors.nightRider};
                  `
                default:
                  return css``
              }
            case ActionButtonVariant.danger:
              switch (props.$look) {
                case ActionButtonLook.contained:
                  return css`
                    background: ${(props) => chroma(props.theme.colors.crimson).darken(0.2).hex()};
                  `
                case ActionButtonLook.outlined:
                  return css`
                    border-color: ${(props) => props.theme.colors.crimson};
                    color: ${(props) => props.theme.colors.pureWhite};
                    background-color: ${(props) => props.theme.colors.crimson};
                  `
                default:
                  return css``
              }
          }
        }}
    `}

  ${(props) =>
    props.disabled &&
    css`
      border: 1px solid ${(props) => props.theme.colors.silver};
      color: ${(props) => props.theme.colors.pureWhite};
      background-color: ${(props) => props.theme.colors.silver};
      cursor: not-allowed;
    `}
`

export const Label = styled.span<{ $noTextOnMobile: boolean }>`
  ${({ theme }) => theme.textStyles.textLargeStrong}
  display: ${(props) => (props.$noTextOnMobile ? 'none' : 'flex')};
  align-items: center;
  min-height: 2.4rem;

  ${breakpoints.up('tablet')} {
    display: flex;
  }
`

export const ActionIcon = styled(Icon)<{ $noIconOnDesktop: boolean }>`
  width: 2.4rem;
  height: 2.4rem;

  ${breakpoints.up('tablet')} {
    display: ${(props) => (props.$noIconOnDesktop ? 'none' : 'block')};
  }
`
